<template>
  <div ref="modal" class="modal fade" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">E-Mail-Adressen verwalten</h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between">
            <span><em class="align-middle material-icons">error_outlined</em> {{ errorMessage }}</span>
            <button aria-label="Close" class="btn-close" type="button" @click.prevent="errorMessage=''"></button>
          </div>

          <div v-if="step === 'list'">
            <p class="small text-muted">
              Diese E-Mail-Adressen haben Zugriff auf dein Konto und erhalten die Messdienerpläne.
            </p>
            <ul class="list-group mb-3">
              <li v-for="row in emails" :key="row.address"
                  class="list-group-item d-flex justify-content-between align-items-center">
                <span>
                  {{ row.address }}
                  <span v-if="row.is_current" class="badge bg-primary ms-2">aktuell</span>
                </span>
                <small class="text-muted">{{ row.servers.join(', ') }}</small>
              </li>
            </ul>
          </div>

          <div v-else-if="step === 'email'">
            <p class="small text-muted">
              {{ mode === 'add'
                ? 'Gib die Adresse ein, die zusätzlich Zugriff erhalten soll. Wir senden einen Bestätigungscode dorthin.'
                : 'Gib die neue Adresse ein, die deine aktuelle Adresse ersetzen soll. Wir senden einen Bestätigungscode dorthin.' }}
            </p>
            <div class="mb-3">
              <label class="form-label" for="newEmail">Neue E-Mail</label>
              <input id="newEmail" v-model="newEmail" autofocus class="form-control" placeholder="Neue E-Mail"
                     type="email" required>
            </div>
          </div>

          <div v-else-if="step === 'code'">
            <p>Wir haben dir einen Code an {{ newEmail }} gesendet.</p>
            <div class="mb-3">
              <label class="form-label" for="changeCode">Code</label>
              <input id="changeCode" v-model="code" autofocus class="form-control" inputmode="numeric"
                     maxlength="6" placeholder="6-stelliger Code" required type="text">
            </div>
            <a class="link" @click.prevent="backToList">Zurück</a>
          </div>

          <div v-else-if="step === 'confirm-remove'">
            <p>
              Möchtest du die Adresse <strong>{{ current }}</strong> wirklich entfernen?
              Du wirst danach abgemeldet.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <template v-if="step === 'list'">
            <button class="btn btn-outline-secondary" type="button" @click="startAdd">
              <em class="align-middle material-icons">add</em> Adresse hinzufügen
            </button>
            <button class="btn btn-outline-primary" type="button" @click="startReplace">
              <em class="align-middle material-icons">edit</em> Aktuelle ändern
            </button>
            <button class="btn btn-outline-danger" type="button" :disabled="emails.length <= 1"
                    @click="step = 'confirm-remove'">
              <em class="align-middle material-icons">delete</em> Aktuelle entfernen
            </button>
          </template>
          <template v-else-if="step === 'email'">
            <button class="btn btn-secondary" type="button" @click="backToList">Zurück</button>
            <button class="btn btn-primary" type="button" :disabled="!newEmail" @click="requestCode">
              <em class="align-middle material-icons">mail</em> Code senden
            </button>
          </template>
          <template v-else-if="step === 'code'">
            <button class="btn btn-secondary" type="button" @click="backToList">Abbrechen</button>
            <button class="btn btn-primary" type="button" :disabled="code.length !== 6" @click="verify">
              <em class="align-middle material-icons">check</em> Bestätigen
            </button>
          </template>
          <template v-else-if="step === 'confirm-remove'">
            <button class="btn btn-secondary" type="button" @click="backToList">Abbrechen</button>
            <button class="btn btn-danger" type="button" @click="doRemove">
              <em class="align-middle material-icons">delete</em> Entfernen
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {Modal} from 'bootstrap';
import {useSelfServiceStore} from "@/stores/selfService";

export default {
  name: "SelfServiceEmailChange",
  emits: ['changed'],
  setup() {
    return {selfServiceStore: useSelfServiceStore()};
  },
  data() {
    return {
      step: 'list',
      mode: 'add',
      emails: [],
      current: '',
      newEmail: '',
      code: '',
      errorMessage: '',
    };
  },
  methods: {
    async open() {
      this.resetForm();
      this.step = 'list';
      this.errorMessage = '';
      await this.loadList();
      this.modalInstance.show();
    },
    close() {
      this.modalInstance.hide();
    },
    resetForm() {
      this.mode = 'add';
      this.newEmail = '';
      this.code = '';
    },
    async loadList() {
      try {
        const data = await this.selfServiceStore.listEmails();
        this.emails = data.emails;
        this.current = data.current;
      } catch (e) {
        this.errorMessage = (e.response && e.response.data.detail) || "Es ist ein Fehler aufgetreten.";
      }
    },
    backToList() {
      this.resetForm();
      this.errorMessage = '';
      this.step = 'list';
    },
    startAdd() {
      this.resetForm();
      this.mode = 'add';
      this.step = 'email';
    },
    startReplace() {
      this.resetForm();
      this.mode = 'replace';
      this.step = 'email';
    },
    async requestCode() {
      this.errorMessage = '';
      try {
        await this.selfServiceStore.requestEmailChange(this.newEmail);
        this.step = 'code';
      } catch (e) {
        if (e.response && e.response.status === 429) {
          this.errorMessage = "Zu viele Anfragen. Bitte versuche es später erneut.";
        } else {
          this.errorMessage = (e.response && e.response.data.detail) || "Es ist ein Fehler aufgetreten.";
        }
      }
    },
    async verify() {
      this.errorMessage = '';
      try {
        const data = await this.selfServiceStore.verifyEmailChange(this.newEmail, this.code, this.mode);
        this.emails = data.emails;
        this.current = data.current;
        this.resetForm();
        this.step = 'list';
        this.$emit('changed');
      } catch (e) {
        this.errorMessage = (e.response && e.response.data.detail) || "Es ist ein Fehler aufgetreten.";
      }
    },
    async doRemove() {
      this.errorMessage = '';
      try {
        await this.selfServiceStore.removeEmail();
        this.close();
        this.$emit('changed');
      } catch (e) {
        this.errorMessage = (e.response && e.response.data.detail) || "Es ist ein Fehler aufgetreten.";
        this.step = 'list';
      }
    },
  },
  mounted() {
    this.modalInstance = new Modal(this.$refs.modal);
  },
  beforeUnmount() {
    if (this.modalInstance) this.modalInstance.dispose();
  },
};
</script>

<style scoped>
a.link {
  cursor: pointer;
}
</style>
